import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";

export default function PostDetail() {
    const { id } = useParams();
    const [settings, setSettings] = useState(null);
    const [post, setPost] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data));
        api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch(() => setPost(false));
    }, [id]);

    const share = async () => {
        const url = window.location.href;
        const shareData = { title: post?.title, text: post?.body?.slice(0, 100), url };
        if (navigator.share) {
            try { await navigator.share(shareData); } catch (_) { /* cancelled */ }
        } else {
            navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 2000);
    };

    if (post === false) {
        return (
            <PublicLayout settings={settings}>
                <div className="max-w-3xl mx-auto px-6 py-24">
                    <h1 className="font-display text-3xl">Post not found</h1>
                    <Link to="/announcements" className="underline-link mt-4 inline-block">Back to announcements</Link>
                </div>
            </PublicLayout>
        );
    }
    if (!post) return <PublicLayout settings={settings}><div className="p-24 text-center">Loading…</div></PublicLayout>;

    return (
        <PublicLayout settings={settings}>
            <article className="max-w-3xl mx-auto px-6 py-16" data-testid="post-detail">
                <Link to="/announcements" className="underline-link text-sm text-[hsl(var(--muted-foreground))]">← All posts</Link>
                <div className="mt-6 flex gap-2 items-center">
                    {post.is_announcement && (
                        <span className="text-[10px] font-mono uppercase tracking-widest bg-[hsl(var(--accent))] text-white px-2 py-0.5 rounded">
                            Announcement
                        </span>
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                        {new Date(post.created_at).toLocaleDateString()}
                    </span>
                </div>
                <h1 className="font-display text-4xl sm:text-5xl mt-4">{post.title}</h1>

                <div className="mt-6 flex gap-3">
                    <button onClick={share} data-testid="share-btn" className="pill inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))] transition-colors">
                        <Share2 size={14} /> Share
                    </button>
                    <button onClick={copyLink} data-testid="copy-link-btn" className="pill inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))] transition-colors">
                        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy link"}
                    </button>
                </div>

                {post.image_url && (
                    <img src={post.image_url} alt={post.title} className="w-full max-h-[70vh] object-cover rounded-2xl mt-10" />
                )}

                <div className="mt-10 whitespace-pre-wrap leading-relaxed text-[hsl(var(--foreground))]">
                    {post.body}
                </div>
            </article>
        </PublicLayout>
    );
}
